import { createClient, type SupabaseClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env['VITE_SUPABASE_URL']
const supabaseAnonKey = import.meta.env['VITE_SUPABASE_ANON_KEY']

// Check if Supabase is configured - warn in console but don't crash
// This allows public pages (landing, etc.) to work without Supabase
export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey)

if (!isSupabaseConfigured) {
  console.warn(
    '[Supabase] Missing environment variables. Auth features will not work. ' +
    'Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your environment.'
  )
}

// Create client only if configured
// Using placeholder URL/key that will fail gracefully on actual API calls
export const supabase: SupabaseClient = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-key',
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      storage: typeof window !== 'undefined' ? window.localStorage : undefined,
      flowType: 'pkce',
    },
  }
)

// Helper to check if Supabase is properly configured
function assertSupabaseConfigured() {
  if (!isSupabaseConfigured) {
    throw new Error(
      'Supabase is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY environment variables.'
    )
  }
}

// Auth helper functions
export async function signInWithEmail(email: string, password: string) {
  assertSupabaseConfigured()
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  })
  if (error) throw error
  return data
}

export async function signUpWithEmail(email: string, password: string, fullName?: string) {
  assertSupabaseConfigured()
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    ...(fullName && {
      options: {
        data: {
          full_name: fullName,
        },
      },
    }),
  })
  if (error) throw error
  return data
}

export async function signOut() {
  assertSupabaseConfigured()
  const { error } = await supabase.auth.signOut()
  if (error) throw error
}

export async function getSession() {
  if (!isSupabaseConfigured) return null
  const { data, error } = await supabase.auth.getSession()
  if (error) throw error
  return data.session
}

export async function getUser() {
  assertSupabaseConfigured()
  const { data, error } = await supabase.auth.getUser()
  if (error) throw error
  return data.user
}

/**
 * Update the display name held in Supabase user metadata. This is what the
 * sidebar label and avatar initials read from, so it has to move in step with
 * the `users.full_name` row that the rest of the app renders.
 */
export async function updateUserDisplayName(fullName: string) {
  assertSupabaseConfigured()
  const { data, error } = await supabase.auth.updateUser({
    data: { full_name: fullName },
  })
  if (error) throw error
  return data.user
}

// Listen to auth state changes
export function onAuthStateChange(
  callback: Parameters<typeof supabase.auth.onAuthStateChange>[0]
) {
  return supabase.auth.onAuthStateChange(callback)
}

// ===================
// MFA helpers
// ===================
// Thin wrappers around supabase.auth.mfa.* — keeps call sites consistent and
// gives us one place to add error normalization later. friendlyName is set to
// 'BidContext AI' so the user's authenticator app shows a recognisable issuer.

export async function mfaEnrollTotp() {
  assertSupabaseConfigured()
  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: 'totp',
    friendlyName: 'BidContext AI',
  })
  if (error) throw error
  return data
}

export async function mfaChallenge(factorId: string) {
  assertSupabaseConfigured()
  const { data, error } = await supabase.auth.mfa.challenge({ factorId })
  if (error) throw error
  return data
}

export async function mfaVerify(factorId: string, challengeId: string, code: string) {
  assertSupabaseConfigured()
  const { data, error } = await supabase.auth.mfa.verify({ factorId, challengeId, code })
  if (error) throw error
  return data
}

export async function mfaUnenroll(factorId: string) {
  assertSupabaseConfigured()
  const { data, error } = await supabase.auth.mfa.unenroll({ factorId })
  if (error) throw error
  return data
}

export async function mfaListFactors() {
  assertSupabaseConfigured()
  const { data, error } = await supabase.auth.mfa.listFactors()
  if (error) throw error
  return data
}

export async function mfaGetAal() {
  assertSupabaseConfigured()
  const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
  if (error) throw error
  return data
}
